import React, { Component } from 'react';
import { withTranslation, TFunction } from 'react-i18next';
import { connect } from 'react-redux';
import moment from 'moment';
import { ArrowLeft } from '../../../assets/icons';
import './LeaveRequestBookMakeUpSessionPage.css';

// Redux Actions
import GetLeavesMakeUpSessionActions from '../../../redux/GetLeavesMakeUpSessionRedux';

// Navigation parameters interface
interface LeaveRequestBookMakeUpSessionPageProps {
  t?: TFunction;
  navigate?: (path: string, params?: any) => void;
  goBack?: () => void;
  
  // Navigation parameters (from URL query strings)
  categoryId?: string | null;
  code?: string | null;
  isStudent?: boolean;
  isTherapist?: boolean;
  
  // Location object for URL parameters
  location?: {
    search: string;
    pathname: string;
  };
  
  // Redux Props
  currentSettings?: any;
  getLeavesMakeUpSessionFetching?: boolean;
  getLeavesMakeUpSessionSuccess?: boolean;
  getLeavesMakeUpSessionError?: boolean;
  listLeavesMake?: any;
  makeupdata?: any;
  
  // Redux Actions
  getLeavesMakeUpSession?: (children_id: string, page: number, isTherapist?: boolean) => void;
}

interface LeaveRequestBookMakeUpSessionPageState {
  radioButton: number;
  selected: string | null;
  has_book_make_up_session: boolean | null;
  allow_option_two: boolean | null;
  dataLeave: any[];
  page: number;
  total: number | null;
  isStudent: boolean;
  isTherapist: boolean;
}

class LeaveRequestBookMakeUpSessionPage extends Component<LeaveRequestBookMakeUpSessionPageProps, LeaveRequestBookMakeUpSessionPageState> {
  private scrollContainer: Element | null = null;
  private touchStartY: number = 0;
  private touchEndY: number = 0;
  private isRefreshing: boolean = false;

  constructor(props: LeaveRequestBookMakeUpSessionPageProps) {
    super(props);
    
    // API call in constructor like reference
    const children_id = this.props.currentSettings?.child?.salesforce_contact_id;
    const isTherapist = this.props.isTherapist || false;
    
    if (children_id) {
      this.props.getLeavesMakeUpSession?.(children_id, 1, isTherapist);
    }
    
    this.state = {
      radioButton: 0,
      selected: null,
      has_book_make_up_session: null,
      allow_option_two: null,
      dataLeave: [],
      page: 1,
      total: null,
      isStudent: this.props.isStudent || false,
      isTherapist: isTherapist,
    };
  }

  // Utility function to parse URL parameters
  parseUrlParams = () => {
    // Return props directly since wrapper now handles URL parsing
    return {
      categoryId: this.props.categoryId,
      code: this.props.code,
      isStudent: this.props.isStudent,
      isTherapist: this.props.isTherapist,
    };
  }

  componentDidMount() {
    console.log('LeaveRequestBookMakeUpSessionPage mounted');
    
    // Add scroll listener for infinite scroll
    this.scrollContainer = document.querySelector('.leave-request-book-make-up-session-page .page-content');
    if (this.scrollContainer) {
      this.scrollContainer.addEventListener('scroll', this.handleScroll);
      // Add touch events for pull-to-refresh
      this.scrollContainer.addEventListener('touchstart', this.handleTouchStart);
      this.scrollContainer.addEventListener('touchmove', this.handleTouchMove);
      this.scrollContainer.addEventListener('touchend', this.handleTouchEnd);
    }
  }

  componentWillUnmount() {
    // Remove event listeners
    if (this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.handleScroll);
      this.scrollContainer.removeEventListener('touchstart', this.handleTouchStart);
      this.scrollContainer.removeEventListener('touchmove', this.handleTouchMove);
      this.scrollContainer.removeEventListener('touchend', this.handleTouchEnd);
    }
  }

  // Handle scroll for infinite loading
  handleScroll = () => {
    if (!this.scrollContainer) return;
    
    const { scrollTop, scrollHeight, clientHeight } = this.scrollContainer;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold
    
    if (isNearBottom && this.state.page <= this.props.listLeavesMake?.meta?.last_page - 1) {
      this.loadData();
    }
  }

  // Handle touch start for pull-to-refresh
  handleTouchStart = (e: Event) => {
    const touchEvent = e as TouchEvent;
    this.touchStartY = touchEvent.touches[0].clientY;
  }

  // Handle touch move for pull-to-refresh
  handleTouchMove = (e: Event) => {
    const touchEvent = e as TouchEvent;
    this.touchEndY = touchEvent.touches[0].clientY;
  }

  // Handle touch end for pull-to-refresh
  handleTouchEnd = () => {
    if (this.isRefreshing) return;
    
    const touchDiff = this.touchStartY - this.touchEndY;
    const isPullDown = touchDiff < -50; // 50px threshold for pull down
    const isAtTop = this.scrollContainer?.scrollTop === 0;
    
    if (isPullDown && isAtTop) {
      this.isRefreshing = true;
      this._onRefresh();
      // Reset refreshing flag after a delay
      setTimeout(() => {
        this.isRefreshing = false;
      }, 1000);
    }
  }

  // PHASE 2: Component Update Logic - Now implemented
  componentDidUpdate(prevProps: LeaveRequestBookMakeUpSessionPageProps) {
    if (!this.props.getLeavesMakeUpSessionFetching) {
      if (
        this.props.getLeavesMakeUpSessionSuccess &&
        this.props.getLeavesMakeUpSessionSuccess !== prevProps.getLeavesMakeUpSessionSuccess
      ) {
        console.log('LeaveRequestBookMakeUpSessionPage: Data received:', {
          page: this.state.page,
          isStudent: this.state.isStudent,
          listLeavesMake: this.props.listLeavesMake
        });
        
        if (this.state.page !== 1) {
          // Append new data for pagination
          const newData = this.state.isStudent
            ? this.props.listLeavesMake?.data?.leave_data
            : this.props.listLeavesMake?.data;
          
          this.setState({
            dataLeave: this.state.dataLeave.concat(newData || []),
          });
        } else {
          // Replace data for first page
          const newData = this.state.isStudent
            ? this.props.listLeavesMake?.data?.leave_data
            : this.props.listLeavesMake?.data;
          
          this.setState({
            dataLeave: newData || [],
          });
        }
        
        this.setState({
          total: this.state.isStudent
            ? this.props.listLeavesMake?.meta?.total
            : this.props.listLeavesMake?.data?.length,
        });
      }
    }
  }

  // Handle back button
  handleBackPress = () => {
    this.props.goBack?.();
  }

  // PHASE 3: Navigation Logic - Now implemented
  _onNextPress = () => {
    if (this.state.selected && !this.state.has_book_make_up_session) {
      console.log('LeaveRequestBookMakeUpSessionPage: Navigating to BookMakeUpSessionPage with:', {
        categoryId: this.props.categoryId,
        code: this.props.code,
        leave_id: this.state.selected,
        make_id: this.props.makeupdata?.data?.id,
        allow_option_two: this.state.allow_option_two,
        isStudent: this.props.isStudent,
        isTherapist: this.props.isTherapist,
      });
      
      this.props.navigate?.('/calendar/book-make-up-session', {
        categoryId: this.props.categoryId,
        code: this.props.code,
        leave_id: this.state.selected,
        make_id: this.props.makeupdata?.data?.id,
        allow_option_two: this.state.allow_option_two,
        isStudent: this.props.isStudent,
        isTherapist: this.props.isTherapist,
      });
    } else {
      console.warn('LeaveRequestBookMakeUpSessionPage: Cannot navigate - missing selection or already booked');
    }
  }

  // PHASE 4: Refresh Logic - Now implemented
  _onRefresh = () => {
    console.log('LeaveRequestBookMakeUpSessionPage: Refreshing data');
    this.setState({
      page: 1,
    });
    const children_id = this.props.currentSettings?.child?.salesforce_contact_id;
    if (children_id) {
      this.props.getLeavesMakeUpSession?.(children_id, 1, this.state.isTherapist);
    } else {
      console.warn('LeaveRequestBookMakeUpSessionPage: Cannot refresh - missing children_id');
    }
  }

  // PHASE 5: Load More Logic - Now implemented
  loadData = () => {
    if (this.props.getLeavesMakeUpSessionFetching) {
      console.log('LeaveRequestBookMakeUpSessionPage: Already loading, skipping loadData');
      return;
    }
    if (this.state.page <= this.props.listLeavesMake?.meta?.last_page) {
      console.log('LeaveRequestBookMakeUpSessionPage: Loading more data for page:', this.state.page + 1);
      this.setState({ page: this.state.page + 1 }, () => {
        const children_id = this.props.currentSettings?.child?.salesforce_contact_id;
        if (children_id) {
          this.props.getLeavesMakeUpSession?.(children_id, this.state.page, this.state.isTherapist);
        } else {
          console.warn('LeaveRequestBookMakeUpSessionPage: Cannot load more - missing children_id');
        }
      });
    } else {
      console.log('LeaveRequestBookMakeUpSessionPage: No more pages to load');
    }
  }

  // Handle radio button selection
  handleItemSelect = (item: any) => {
    // PHASE 6: Selection Logic - Now implemented
    const isBooked = this.state.isStudent
      ? item?.has_book_make_up_session
      : item?.hasBooked;
    
    console.log('LeaveRequestBookMakeUpSessionPage: Item selected:', {
      item,
      isBooked,
      allow_option_two: item?.allow_option_two
    });
    
    this.setState({
      selected: item?.id,
      has_book_make_up_session: isBooked,
      allow_option_two: item?.allow_option_two,
    });
  }

  

  // PHASE 7: List Item Rendering - Now implemented
  renderListItem = (item: any, index: number) => {
    const leaveDate = this.state.isStudent
      ? item?.date
        ? moment(item?.date?.date).format("DD MMMM YYYY")
        : ""
      : item?.from_date
      ? moment(item?.from_date).format("DD MMMM YYYY")
      : "";
    const name = this.state.isStudent
      ? item?.name || ""
      : `${item?.first_name} ${item?.last_name}`;
    const time = this.state.isStudent
      ? item?.time || ""
      : `${moment(item?.from_time, "HH:mm:ss").format("HH:mm")} - ${moment(item?.to_time, "HH:mm:ss").format("HH:mm")}`;
    const typeOfLeave = this.state.isStudent
      ? item?.type_of_leave == 1
        ? this.props.t?.('select-leavere-quest:sick-leave')
        : this.props.t?.('select-leavere-quest:other-leave')
      : item?.type;
    const isBooked = this.state.isStudent
      ? item?.has_book_make_up_session
      : item?.hasBooked;
    const countLeave = this.state.dataLeave.length;

    return (
      <div
        key={item?.id || index}
        className={`leave-item ${!isBooked ? 'booked' : ''} ${this.state.selected === item?.id ? 'selected' : ''}`}
        onClick={() => !isBooked && this.handleItemSelect(item)}
      >
        <div className="leave-item-content">
          <div className="leave-item-header">
            <h3 className="leave-item-title">
              {this.state.isStudent
                ? this.props.t?.('verify-request:leave-request')
                : this.props.t?.('verify-request:therapist-leave-request')}{" "}
              {countLeave - index}
            </h3>
          </div>
        
          <div className="leave-item-details">
            <div className="detail-row">
              <span className="detail-label">
                {this.props.t?.('appointment-summary-screen:summary.item.name')}:
              </span>
              <span className="detail-value">{name}</span>
            </div>
          
            <div className="detail-row">
              <span className="detail-label">
                {this.props.t?.('select-leavere-quest:leave-date')}:
              </span>
              <span className="detail-value">{leaveDate}</span>
            </div>
          
            <div className="detail-row">
              <span className="detail-label">
                {this.props.t?.('appointment-summary-screen:summary.item.time')}:
              </span>
              <span className="detail-value">{time}</span>
            </div>
          
            {this.state.isStudent && (
              <div className="detail-row">
                <span className="detail-label">
                  {this.props.t?.('select-leavere-quest:therapist')}:
                </span>
                <span className="detail-value">{item?.therapist || ""}</span>
              </div>
            )}
          
            {this.state.isStudent && (
              <div className="detail-row">
                <span className="detail-label">
                  {this.props.t?.('select-leavere-quest:service-type')}:
                </span>
                <span className="detail-value">{item?.service_type || ""}</span>
              </div>
            )}
          
            <div className="detail-row">
              <span className="detail-label">
                {this.props.t?.('select-leavere-quest:type-of-leave')}:
              </span>
              <span className="detail-value">{typeOfLeave}</span>
            </div>
          
            {this.state.isStudent && item?.type_of_leave == 1 && (
              <div className="detail-row">
                <span className="detail-label">
                  {this.props.t?.('select-leavere-quest:sick-note')}:
                </span>
                <span className="detail-value">{item?.sick_note || " "}</span>
              </div>
            )}
          
            <div className="detail-row">
              <span className="detail-label">
                {this.props.t?.('select-leavere-quest:make-up')}:
              </span>
              <div className="make-up-dates">
                {item?.exist_make_up_session_date?.map((session: any, idx: number) => (
                  <div key={idx} className="make-up-date">
                    {session?.date
                      ? moment(session?.date).format("DD MMMM YYYY")
                      : ""}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {!isBooked && (
          <div className="leave-item-radio">
            {this.state.selected === item?.id ? (
              <div className="radio-selected" />
            ) : (
              <div className="radio-unselected" />
            )}
          </div>
        )}
      </div>
    );
  };

  // Render load more button
  renderLoadMoreButton = () => {
    const { getLeavesMakeUpSessionFetching, listLeavesMake } = this.props;
    const { page } = this.state;
    
    // Don't show if currently loading
    if (getLeavesMakeUpSessionFetching) {
      return null;
    }
    
    // Don't show if no more pages available
    if (!listLeavesMake?.meta || page >= listLeavesMake.meta.last_page) {
      return null;
    }
    
    return (
      <div className="load-more-container">
        <button 
          className="load-more-button"
          onClick={this.loadData}
          disabled={getLeavesMakeUpSessionFetching}
        >
          <span>{this.props.t?.('common:load-more') || 'Load More'}</span>
        </button>
      </div>
    );
  };

  render() {
    const { selected, dataLeave, isStudent, isTherapist } = this.state;
    const { getLeavesMakeUpSessionFetching, listLeavesMake } = this.props;

    // Show loader on initial load (when fetching and no data yet)
    const shouldShowMainLoader = getLeavesMakeUpSessionFetching && dataLeave.length === 0;

    return (
      <div className="leave-request-book-make-up-session-page">
        {/* Header */}
        <div className="page-header">
          <div className="page-header-inner">
            <div className="header-left">
              <button 
                className="header-button" 
                onClick={this.handleBackPress}
              >
                <ArrowLeft />
              </button>
              <h1 className="header-title">
                {this.props.t?.('leave-request-history:leave-request-list') || 'Leave Request List'}
              </h1>
            </div>
            <div className="header-right">
              <button
                className="header-button next-button"
                onClick={this._onNextPress}
                disabled={!selected}
              >
                <span>
                  {this.props.t?.('select-booking-category-screen:header.right') || 'Next'}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="page-content">
          {/* Loading State */}
          {shouldShowMainLoader && (
            <div className="loading-card">
              <div className="loading-spinner">
                <span>{this.props.t?.('common:loading') || 'Loading...'}</span>
              </div>
                          </div>
                        )}
                        
          {/* Error State */}
          {this.props.getLeavesMakeUpSessionError && (
            <div className="error-card">
              <div className="error-message">
                <span>{this.props.t?.('common:error') || 'Error loading data. Please try again.'}</span>
                        </div>
                          </div>
                        )}
                        
          {/* PHASE 8: List Content - Now implemented */}
          <div className="leave-list">
            {dataLeave.map((item, index) => this.renderListItem(item, index))}
          </div>

          {/* Load More Button */}
          {this.renderLoadMoreButton()}
          
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    currentSettings: state.currentSettings,
    makeupdata: state.createMakeUpSession?.makeupdata,
    getLeavesMakeUpSessionFetching: state.getLeavesMakeUpSession?.fetching,
    getLeavesMakeUpSessionSuccess: state.getLeavesMakeUpSession?.success,
    getLeavesMakeUpSessionError: state.getLeavesMakeUpSession?.error,
    listLeavesMake: state.getLeavesMakeUpSession?.listLeavesMake,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    getLeavesMakeUpSession: (children_id: string, page: number, isTherapist?: boolean) =>
      dispatch(GetLeavesMakeUpSessionActions.getLeavesMakeUpSessionRequest(children_id, page, isTherapist)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(LeaveRequestBookMakeUpSessionPage));